import { useSite } from '../lib/site-context.jsx';

const Email = ({ settings }) => (settings.general_email ? <a href={`mailto:${settings.general_email}`}>{settings.general_email}</a> : <>the contact address on the Contact page</>);

export function Privacy() {
  const { settings, year } = useSite();
  const company = settings.company_name || 'KeysGrove';
  return (
    <section className="section legal">
      <div className="wrap narrow prose">
        <span className="section-eyebrow">Legal</span>
        <h1>Privacy Policy</h1>
        <p className="muted">Last updated {year}. This policy applies to {company} and all listings on this site.</p>

        <h2>1. Information we collect</h2>
        <p>When you submit an enquiry we collect your name, phone number, email address (optional), and the project or budget range you indicated. We also automatically capture UTM parameters from the URL if you arrived via an advertisement — solely for internal analytics, never shared outside the platform.</p>

        <h2>2. How we use your information</h2>
        <p>Your enquiry details are shared with the real-estate company whose listing you enquired about (the &ldquo;tenant&rdquo;) so they can follow up with you about the property. We do not sell or rent your personal data to any third party.</p>
        <p>If you opted in to listing alerts via the newsletter form, we use your email address to send you new listing notifications. You can unsubscribe at any time by clicking the link in any alert email.</p>

        <h2>3. Data retention</h2>
        <p>Enquiry records are retained by the tenant for as long as they maintain an active account on this platform. If you would like your information corrected or deleted, contact the company you enquired with directly, or reach us at the address in section 8.</p>

        <h2>4. Cookies and browser storage</h2>
        <p>We use a single HttpOnly, Secure, SameSite=Strict session cookie for admin-panel authentication — set only after a successful login, expiring after 12 hours. We do not use tracking cookies, advertising cookies, or third-party analytics scripts. The language preference you choose (English / Hindi) is stored only in your browser&apos;s local storage and never sent to the server.</p>

        <h2>5. Security</h2>
        <p>All traffic is served over HTTPS. Admin passwords are stored as salted hashes; we never store plain-text passwords. Session tokens are HMAC-signed and verified server-side on every request. Optional two-factor authentication (TOTP) is available to all admin accounts.</p>

        <h2>6. Your rights</h2>
        <p>You may request access to, correction of, or deletion of your personal data by contacting the real-estate company you enquired with, or by writing to us at the email below. We will respond within 30 days.</p>

        <h2>7. Changes to this policy</h2>
        <p>We may update this policy from time to time. Material changes will be noted on this page. Continued use of the site after an update constitutes acceptance of the revised policy.</p>

        <h2>8. Contact</h2>
        <p>Privacy questions: <Email settings={settings} />.</p>
      </div>
    </section>
  );
}

export function Terms() {
  const { settings, year } = useSite();
  const company = settings.company_name || 'KeysGrove';
  return (
    <section className="section legal">
      <div className="wrap narrow prose">
        <span className="section-eyebrow">Legal</span>
        <h1>Terms of Use</h1>
        <p className="muted">Last updated {year}. By using {company}, you agree to these terms.</p>

        <h2>1. About the platform</h2>
        <p>{company} is an online real estate listing platform. We aggregate property listings from real-estate companies (&ldquo;tenants&rdquo;) operating across Noida, Gurugram and the Yamuna Expressway corridor. We are not a real-estate broker or developer ourselves — we do not buy, sell or hold property.</p>

        <h2>2. Listing accuracy</h2>
        <p>Listing details — including price, area, amenities, RERA registration number and possession date — are entered by the respective tenant and are presented as-is. We make reasonable efforts to ensure accuracy but cannot guarantee that every detail is current. Prices and availability are subject to change without notice. Always verify with the developer or broker before making any financial decision.</p>

        <h2>3. RERA compliance</h2>
        <p>Projects listed on this platform are required to be RERA-registered. If you believe a listing is inaccurate or non-compliant, please contact us at the email in section 9 so we can review it promptly.</p>

        <h2>4. Enquiry submission</h2>
        <p>By submitting an enquiry you consent to being contacted by the real-estate company whose listing you enquired about via phone, email or WhatsApp. You may withdraw consent at any time by asking the company to remove you from their records.</p>

        <h2>5. Acceptable use</h2>
        <p>You may not use this platform to submit false or misleading enquiries, to scrape listing data in bulk, to attempt to access admin areas without authorisation, or for any unlawful purpose. Automated access without prior written permission is prohibited.</p>

        <h2>6. Intellectual property</h2>
        <p>The {company} name, wordmark, design and site code are proprietary. Project images, brochures and descriptions remain the property of the respective developer or tenant. Nothing here grants you a licence to reproduce or redistribute any content from this site without written permission.</p>

        <h2>7. Limitation of liability</h2>
        <p>To the maximum extent permitted by applicable law, {company} and its operators are not liable for any loss or damage arising from reliance on listing information, failed transactions, or decisions made based on content on this site.</p>

        <h2>8. Governing law</h2>
        <p>These terms are governed by the laws of India. Any dispute arising from these terms shall be subject to the jurisdiction of the courts of Delhi/NCR.</p>

        <h2>9. Contact</h2>
        <p>Questions about these terms: <Email settings={settings} />.</p>
      </div>
    </section>
  );
}
